import { useMemo, useState, type FormEvent } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { changePassword } from "@/api/profile";
import { BrandMark } from "@/components/BrandMark";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { useAuthStore } from "@/store/authStore";

/**
 * Shown instead of the app while `me.must_change_password` is true — i.e. the
 * account is on an admin-issued temporary password. The API refuses every
 * permission-gated endpoint until it is replaced, so there is nothing useful
 * to render behind this screen.
 */
export function ForcePasswordChangePage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { me, loadMe, refreshAccess, signOut } = useAuthStore();

  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => changePassword(current, password),
    // The flag is cleared server-side, but the access token in hand still
    // carries must_change_password=true — every permission-gated endpoint would
    // keep answering 403 until it expires. Reissue it first, then re-read
    // /auth/me to unmount this screen and reveal the app.
    onSuccess: async () => {
      await refreshAccess();
      await loadMe();
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } }).response
        ?.data?.detail;
      setError(detail || "error");
    },
  });

  const mismatch = useMemo(
    () => confirm.length > 0 && password !== confirm,
    [password, confirm],
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mismatch) {
      setError("password_mismatch");
      return;
    }
    if (password.length < 4) {
      setError("password_too_short");
      return;
    }
    if (password === current) {
      setError("new_password_must_differ");
      return;
    }
    mutation.mutate();
  };

  const bg = theme.palette.mode === "dark" ? "/brand/bg-dark.svg" : "/brand/bg-light.svg";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        px: 2,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ position: "absolute", top: 16, right: 16 }}>
        <LocaleSwitcher />
        <ThemeSwitcher />
      </Stack>

      <Card
        sx={{
          width: "100%",
          maxWidth: 440,
          backdropFilter: "blur(8px)",
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(30, 30, 30, 0.85)"
              : "rgba(255, 255, 255, 0.92)",
          borderColor: "divider",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <Stack alignItems="center" spacing={1.5}>
              <BrandMark variant="wordmark" height={56} />
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                {t("forcePassword.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {t("forcePassword.subtitle")}
              </Typography>
              {me?.email && (
                <Typography variant="caption" color="text.secondary">
                  {me.email}
                </Typography>
              )}
            </Stack>

            <Box component="form" onSubmit={onSubmit}>
              <Stack spacing={2}>
                {error && (
                  <Alert severity="error">
                    {t(`forcePassword.err.${error}`, { defaultValue: error })}
                  </Alert>
                )}

                <TextField
                  label={t("forcePassword.current")}
                  type={showPwd ? "text" : "password"}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                  fullWidth
                  required
                />

                <TextField
                  label={t("forcePassword.password")}
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  fullWidth
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPwd((v) => !v)}
                          edge="end"
                          aria-label={t("forcePassword.toggleVisibility")}
                        >
                          {showPwd ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label={t("forcePassword.confirm")}
                  type={showPwd ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  fullWidth
                  required
                  error={mismatch}
                  helperText={mismatch ? t("forcePassword.err.password_mismatch") : " "}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={mutation.isPending || !current || !password || mismatch}
                >
                  {t("forcePassword.submit")}
                </Button>

                <Button size="small" color="inherit" onClick={() => void signOut()}>
                  {t("forcePassword.signOut")}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
